import { Button, SpaceBetween, Spinner } from "@cloudscape-design/components";
import { TextInput } from "../../../../components/TextInput";
import { DropdownBox } from "../../../../components/DropdownBox";
import PhoneInput from 'react-phone-input-2'
import { Multiselect } from "../../../../components/Multiselect";
import { contactOptions } from "../../QuestionPages/Questions/questionOptions";
import cipraLogo from '../../../../res/ProviderLogos/cipra_logo.jpg';

// Custom questions
import renderBPcuffQuestion from "../questions/QuestionBloodPressureCuff";
import renderGlucometerQuestion from "../questions/QuestionGlucometer";

// API Response
import { AdminRegistrationAPIResponse } from "../types"


type Props = {
    adminEmailValue: string;
    setAdminEmailValue: (value: string) => void;
    validAdminEmail: boolean;

    adminPasswordValue: string;
    setAdminPasswordValue: (value: string) => void;
    validAdminPassword: boolean;

    adminSignedIn: boolean;
    adminStatusMessage: JSX.Element | undefined;
    submitAdminLogin: () => void;
    apiResponse: AdminRegistrationAPIResponse | null;

    selectedLanguage: string;
    setSelectedLanguage: (value: string) => void;
    validSelectedLanguage: boolean;

    selectedProvider: string;
    setSelectedProvider: (value: string) => void;
    validSelectedProvider: boolean;

    selectedPrograms: string[];
    setSelectedPrograms: (value: string[]) => void;
    validSelectedPrograms: boolean

    firstNameValue: string;
    setFirstNameValue: (value: string) => void;
    validFirstName: boolean;

    lastNameValue: string;
    setLastNameValue: (value: string) => void;
    validLastName: boolean;

    phoneValue: string;
    setPhoneValue: (value: string) => void;
    validPhoneNumber: boolean;

    emailValue: string;
    setEmailValue: (value: string) => void;
    validEmail: boolean;

    preferredContactMethodValue: string[];
    setPreferredContactMethod: (value: string[]) => void;
    validPreferredContactMethod: boolean;

    bpCuffValue: string;
    setBpCuffValue: (value: string) => void;
    validBpCuff: boolean;

    glucometerValue: string;
    setGlucometerValue: (value: string) => void;
    validGlucometer: boolean;

    subscription: string;
    setSubscription: (value: string) => void;
    validSubscription: boolean;

    statusMessage: JSX.Element | undefined;
    registerPatient: () => void;
    submissionInProgress: boolean;
};

function RegistrationFormAdmin(props: Props) {
    const {
        adminEmailValue,
        setAdminEmailValue,
        validAdminEmail,
        adminPasswordValue,
        setAdminPasswordValue,
        validAdminPassword,
        adminSignedIn,
        adminStatusMessage,
        submitAdminLogin,
        apiResponse,
        selectedLanguage,
        setSelectedLanguage,
        validSelectedLanguage,
        selectedProvider,
        setSelectedProvider,
        validSelectedProvider,
        selectedPrograms,
        setSelectedPrograms,
        validSelectedPrograms,
        firstNameValue,
        setFirstNameValue,
        validFirstName,
        lastNameValue,
        setLastNameValue,
        validLastName,
        phoneValue,
        setPhoneValue,
        validPhoneNumber,
        emailValue,
        setEmailValue,
        validEmail,
        preferredContactMethodValue,
        setPreferredContactMethod,
        validPreferredContactMethod,
        bpCuffValue,
        setBpCuffValue,
        validBpCuff,
        glucometerValue,
        setGlucometerValue,
        validGlucometer,
        subscription,
        setSubscription,
        validSubscription,
        statusMessage,
        registerPatient,
        submissionInProgress
    } = props;

    return (
        <div className="center-screen">
            <div className="glow-border page-container" style={{ maxWidth: "330px" }}>
                <SpaceBetween size='m' direction='vertical'>
                    <img src={cipraLogo} alt='cipra logo' width='90%' className='line-align-center' />

                    {/* Before admin signs in */}
                    {
                        !adminSignedIn &&
                        <SpaceBetween size='m' direction='vertical'>
                            <h2 className='line-align-center'>Log In</h2>
                            <TextInput
                                label="Email"
                                value={adminEmailValue}
                                onChange={setAdminEmailValue}
                                type="email"
                                placeholder="Email Address"
                                showMessage={!validAdminEmail}
                            />
                            <TextInput
                                label="Password"
                                value={adminPasswordValue}
                                onChange={setAdminPasswordValue}
                                type="password"
                                placeholder="Password"
                                showMessage={!validAdminPassword}
                                showPasswordToggle
                            />

                            {adminStatusMessage && <p className='tip-text'>{adminStatusMessage}</p>}

                            <div className='line-align-center'>
                                <Button variant='primary' onClick={submitAdminLogin} disabled={submissionInProgress}>
                                    {submissionInProgress && <Spinner />}
                                    Log In
                                </Button>
                            </div>
                        </SpaceBetween>
                    }

                    {/* After admin signs in */}
                    {adminSignedIn && apiResponse &&
                        <SpaceBetween size='m' direction='vertical'>
                            <DropdownBox
                                label="Select a Language"
                                options={apiResponse.languages.map(language => ({ value: language.language_id, label: language.language_name }))}
                                selectedValue={selectedLanguage}
                                onChange={setSelectedLanguage}
                                showMessage={!validSelectedLanguage}
                            />

                            <DropdownBox
                                label="Select a Provider"
                                options={apiResponse.providers.map(provider => ({ value: provider.provider, label: provider.name }))}
                                selectedValue={selectedProvider}
                                onChange={setSelectedProvider}
                                showMessage={!validSelectedProvider}
                            />

                            <Multiselect
                                label="Select Programs"
                                options={apiResponse.programs.map(program => ({ value: program.program, label: `${program.name} (${program.program})` }))}
                                selectedValue={selectedPrograms}
                                onChange={setSelectedPrograms}
                                showMessage={!validSelectedPrograms}
                            />

                            <SpaceBetween size='xxxs' direction='vertical'>
                                <div>Name</div>
                                <SpaceBetween size='xxxs' direction='horizontal'>
                                    <TextInput
                                        value={firstNameValue}
                                        onChange={setFirstNameValue}
                                        placeholder="First Name"
                                        showMessage={!validFirstName}
                                    />
                                    <TextInput
                                        value={lastNameValue}
                                        onChange={setLastNameValue}
                                        placeholder="Last Name"
                                        showMessage={!validLastName}
                                    />
                                </SpaceBetween>
                            </SpaceBetween>

                            <SpaceBetween size='m' direction='horizontal'>
                                <div>Phone Number</div>
                            </SpaceBetween>

                            <PhoneInput
                                country={'us'}
                                value={phoneValue}
                                onChange={setPhoneValue}
                                buttonStyle={{
                                    borderTopLeftRadius: '8px',
                                    borderBottomLeftRadius: '8px',
                                    borderTop: '2px solid #9ba7b6',
                                    borderBottom: '2px solid #9ba7b6',
                                    borderLeft: '2px solid #9ba7b6'
                                }}
                                inputStyle={{
                                    width: '100%',
                                    borderTopRightRadius: '8px',
                                    borderBottomRightRadius: '8px',
                                    borderTop: '2px solid #9ba7b6',
                                    borderBottom: '2px solid #9ba7b6',
                                    borderRight: '2px solid #9ba7b6'
                                }}
                                placeholder="Phone number"
                            />
                            {!validPhoneNumber && <p className='tip-text'>Please enter a valid phone number.</p>}

                            <TextInput
                                label="Email"
                                value={emailValue}
                                onChange={setEmailValue}
                                type="email"
                                placeholder="Email Address"
                                showMessage={!validEmail}
                                message="Please enter a valid email."
                            />

                            <Multiselect
                                label="Which contact methods do you prefer? (Select all that apply)"
                                options={contactOptions}
                                selectedValue={preferredContactMethodValue}
                                onChange={setPreferredContactMethod}
                                showMessage={!validPreferredContactMethod}
                            />

                            {renderBPcuffQuestion(
                                selectedPrograms.includes("HTN"), // show_bp_cuff_question - depends on program selection
                                false, // provide_bp_cuff_option
                                false, // bp_cuff_require_bluetooth_option
                                bpCuffValue,
                                setBpCuffValue,
                                validBpCuff
                            )}

                            {renderGlucometerQuestion(
                                (selectedPrograms.includes("DM") || selectedPrograms.includes("DM_HYBRID")), // show_glucometer_question - depends on program selection
                                false, // provide_glucometer_option
                                !selectedPrograms.includes("DM_HYBRID"), // finger_prick_option - depends on program selection
                                glucometerValue,
                                setGlucometerValue,
                                validGlucometer
                            )}

                
                            {adminEmailValue === "support.in@cipra.ai" && (
                                <DropdownBox
                                    label="Select Subscription Type"
                                    options={[
                                        { value: "6", label: "6 months" },
                                        { value: "12", label: "12 months" }
                                    ]}
                                    selectedValue={subscription}
                                    onChange={setSubscription}
                                    showMessage={!validSubscription}
                                />
                            )}
                            

                            {statusMessage && <p className='tip-text'>{statusMessage}</p>}

                            <div className='line-align-center'>
                                <Button variant='primary' onClick={registerPatient} disabled={submissionInProgress}>
                                    {submissionInProgress && <Spinner />}
                                    Confirm
                                </Button>
                            </div>
                        </SpaceBetween>
                    }
                </SpaceBetween>
            </div>
        </div>
    );
}

export default RegistrationFormAdmin;